import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { AppErrors } from '@fitness-force/models';;
import type {
  ProductCategoryResponse,
  CreateProductCategoryRequest,
  UpdateProductCategoryRequest,
  ListProductCategoriesResponse,
  ListProductCategoriesRequest,
  CreateProductCategoryResponse,
  UpdateProductCategoryResponse
} from '../models';
import { environment } from '@fitness-force/environments';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';

@Injectable({
  providedIn: 'root'
})
export class ProductCategoryService {
  private http = inject(HttpClient);

  private readonly API_BASE_URL = environment.RETAIL_BILLING_URL;

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  private buildParams(params: Record<string, any>): HttpParams {
    let httpParams = new HttpParams();
    
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== null && value !== undefined) {
        httpParams = httpParams.set(key, value.toString());
      }
    });
    
    return httpParams;
  }

  // Company-level category operations
  getCompanyCategories(request?: ListProductCategoriesRequest): Observable<ListProductCategoriesResponse> {
    // Map the request to API-expected parameters
    const params: any = {
      PageNumber: request?.page || 1,
      PageSize: request?.page_size || 10
    };
    
    // Add optional search parameters
    if (request?.search_term) {
      params.SearchTerm = request.search_term;
    }
    
    return this.http.get<ListProductCategoriesResponse>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.RETAIL.GET_PRODUCT_CATEGORIES,
      {
        headers: this.httpOptions.headers,
        params: this.buildParams(params)
      }
    ).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Company Categories API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getCompanyCategory(categoryId: number): Observable<ProductCategoryResponse> {
    const params = {
      CompanyId: 50
    };

    return this.http.get<ProductCategoryResponse>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.RETAIL.GET_PRODUCT_CATEGORY_BY_ID
        .replace('{category_id}', categoryId.toString()),
      {
        headers: this.httpOptions.headers,
        params: this.buildParams(params)
      }
    ).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Company Category API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  createCompanyCategory(request: CreateProductCategoryRequest): Observable<CreateProductCategoryResponse> {
    return this.http.post<CreateProductCategoryResponse>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.RETAIL.CREATE_PRODUCT_CATEGORY,
      request,
      {
        headers: this.httpOptions.headers
      }
    ).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Create Company Category API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  updateCompanyCategory(categoryId: number, request: UpdateProductCategoryRequest): Observable<UpdateProductCategoryResponse> {
    return this.http.put<UpdateProductCategoryResponse>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.RETAIL.UPDATE_PRODUCT_CATEGORY
        .replace('{category_id}', categoryId.toString()),
      request,
      {
        headers: this.httpOptions.headers
      }
    ).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Update Company Category API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }
}